import { useCallback, useEffect, useMemo, useRef } from "react"
import { useNavigate } from "@tanstack/react-router"
import { zodResolver } from "@hookform/resolvers/zod"
import { useFieldArray, useForm } from "react-hook-form"
import { z } from "zod"
import { isAfter, parseISO } from "date-fns"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { DatePicker } from "@/components/ui/date-picker"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  useIsSavingMembershipApplicationDraft,
  useMembershipApplicationDraft,
  useSaveMembershipApplicationDraft,
  type EmploymentHistoryDraftEntry,
} from "../api/useMembershipApplicationDraft"
import { membershipApplicationSteps } from "../constants"
import { useApplicationSectionConfiguration } from "../hooks/useApplicationConfiguration"
import { RequirementSummaryList } from "./requirement-summary-list"

const employmentEntrySchema = z
  .object({
    id: z.string().optional(),
    organisation: z.string().trim().min(2, "Enter the organisation name"),
    jobTitle: z.string().trim().min(2, "Enter your job title"),
    startDate: z.string().min(1, "Select a start date"),
    endDate: z.string().optional(),
    currentlyEmployed: z.boolean().default(false),
    responsibilities: z.string().trim().max(1000, "Keep responsibilities under 1,000 characters").optional(),
    reportingTo: z.string().trim().max(160, "Keep the reporting contact under 160 characters").optional(),
  })
  .superRefine((data, ctx) => {
    const start = parseDate(data.startDate)
    if (!start) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Enter a valid start date",
        path: ["startDate"],
      })
    }

    if (!data.currentlyEmployed && (!data.endDate || data.endDate.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Provide an end date or mark as currently employed",
        path: ["endDate"],
      })
    }

    if (data.endDate && data.endDate.length > 0) {
      const end = parseDate(data.endDate)
      if (!end) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Enter a valid end date",
          path: ["endDate"],
        })
      } else if (start && isAfter(start, end)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "End date must be after start date",
          path: ["endDate"],
        })
      }
    }
  })

const employmentDraftSchema = z.object({
  entries: z.array(employmentEntrySchema),
})

const employmentFormSchema = employmentDraftSchema.refine((data) => data.entries.length > 0, {
  message: "Add at least one employment record",
  path: ["entries"],
})

export type EmploymentFormValues = z.infer<typeof employmentDraftSchema>

type EmploymentDraftValues = z.infer<typeof employmentDraftSchema>

function parseDate(value: string | undefined): Date | null {
  if (!value) return null
  const parsed = parseISO(value)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }
  return parsed
}

function mapDraftToFormValues(
  entries: EmploymentHistoryDraftEntry[],
  options?: { ensureEntry?: boolean },
): EmploymentDraftValues {
  const { ensureEntry = true } = options ?? {}
  if (!entries || entries.length === 0) {
    return { entries: ensureEntry ? [createEmptyEmploymentFormEntry()] : [] }
  }

  return {
    entries: entries.map((entry) => ({
      id: entry.id,
      organisation: entry.organisation ?? "",
      jobTitle: entry.jobTitle ?? "",
      startDate: entry.startDate ?? "",
      endDate: entry.endDate ?? "",
      currentlyEmployed: Boolean(entry.currentlyEmployed),
      responsibilities: entry.responsibilities ?? "",
      reportingTo: entry.reportingTo ?? "",
    })),
  }
}

function mapFormEntriesToDraft(entries: EmploymentDraftValues["entries"]): EmploymentHistoryDraftEntry[] {
  return entries.map((entry) => ({
    id: entry.id ?? cryptoId(),
    organisation: entry.organisation.trim(),
    jobTitle: entry.jobTitle.trim(),
    startDate: entry.startDate,
    endDate: entry.currentlyEmployed ? undefined : entry.endDate?.trim() || undefined,
    currentlyEmployed: entry.currentlyEmployed,
    responsibilities: entry.responsibilities?.trim() || undefined,
    reportingTo: entry.reportingTo?.trim() || undefined,
  }))
}

function createEmptyEmploymentFormEntry() {
  return {
    id: cryptoId(),
    organisation: "",
    jobTitle: "",
    startDate: "",
    endDate: "",
    currentlyEmployed: false,
    responsibilities: "",
    reportingTo: "",
  }
}

function cryptoId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID()
    }
  } catch (_error) {
    // ignore
  }
  return Math.random().toString(36).slice(2, 12)
}

export function EmploymentStep() {
  const navigate = useNavigate()
  const { data: draft } = useMembershipApplicationDraft()
  const saveDraftMutation = useSaveMembershipApplicationDraft()
  const isSavingDraft = useIsSavingMembershipApplicationDraft()

  const categoryId = draft?.category?.categoryId ?? null
  const {
    section,
    isVisible,
    isRequired,
    nextStep,
    categoriesQuery,
    category,
  } = useApplicationSectionConfiguration("employment", categoryId)

  useEffect(() => {
    if (categoriesQuery.isLoading) {
      return
    }
    if (!isVisible) {
      const fallbackPath = nextStep?.path ?? membershipApplicationSteps[membershipApplicationSteps.length - 1]?.path
      if (fallbackPath) {
        navigate({ to: fallbackPath, replace: true })
      }
    }
  }, [categoriesQuery.isLoading, isVisible, nextStep, navigate])

  if (!isVisible && !categoriesQuery.isLoading) {
    return null
  }

  const formDefaultValues = useMemo(
    () =>
      mapDraftToFormValues(draft?.employmentHistory ?? [], {
        ensureEntry: isRequired,
      }),
    [draft?.employmentHistory, isRequired],
  )

  const schema = isRequired ? employmentFormSchema : employmentDraftSchema
  const resolver = useMemo(() => zodResolver(schema), [schema])

  const form = useForm<EmploymentFormValues>({
    resolver,
    defaultValues: formDefaultValues,
    mode: "onChange",
  })

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "entries",
  })

  useEffect(() => {
    form.reset(formDefaultValues)
  }, [formDefaultValues, form])

  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const skipNextAutosaveRef = useRef(true)

  useEffect(() => {
    skipNextAutosaveRef.current = true
  }, [formDefaultValues])

  useEffect(() => {
    const subscription = form.watch((values, info) => {
      if (skipNextAutosaveRef.current) {
        skipNextAutosaveRef.current = false
        return
      }

      if (info?.type !== "change" && info?.type !== "blur") {
        return
      }

      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current)
      }

      autosaveTimerRef.current = setTimeout(() => {
        const parsed = employmentDraftSchema.safeParse(values)
        if (!parsed.success) {
          return
        }

        const payload = mapFormEntriesToDraft(parsed.data.entries)
        saveDraftMutation.mutate({ employmentHistory: payload })
      }, 800)
    })

    return () => {
      subscription.unsubscribe()
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current)
      }
    }
  }, [form, saveDraftMutation])

  const nextStepPath = nextStep?.path

  const handleSubmit = useCallback(
    async (values: EmploymentFormValues) => {
      const payload = mapFormEntriesToDraft(values.entries)
      await saveDraftMutation.mutateAsync({ employmentHistory: payload })

      if (nextStepPath) {
        navigate({ to: nextStepPath })
      }
    },
    [navigate, nextStepPath, saveDraftMutation],
  )

  const isBusy = isSavingDraft || saveDraftMutation.isPending

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold">
              Employment history
              {!isRequired ? (
                <span className="ml-2 text-sm font-medium text-muted-foreground">Optional</span>
              ) : null}
            </h2>
            <p className="text-sm text-muted-foreground">
              {section?.guidance ?? "Record your professional experience in chronological order."}
            </p>
            {!isRequired ? (
              <p className="text-xs text-muted-foreground">
                {category?.categoryName
                  ? `${category.categoryName} applicants may skip this section if it does not apply.`
                  : "You may skip this section if it does not apply."}
              </p>
            ) : null}
          </div>
          <Button type="button" variant="outline" onClick={() => append(createEmptyEmploymentFormEntry())}>
            Add employment
          </Button>
        </div>

        <div className="space-y-4">
          {section?.requirementSummaries && section.requirementSummaries.length > 0 ? (
            <RequirementSummaryList
              title="What to include"
              summaries={section.requirementSummaries}
            />
          ) : null}

          {fields.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                No employment entries yet. Add your first role to begin.
              </CardContent>
            </Card>
          ) : null}

          {fields.map((entry, index) => {
            const currentlyEmployed = form.watch(`entries.${index}.currentlyEmployed`)
            const fieldArrayId = entry.id ?? `${index}`
            const checkboxId = `currently-employed-${fieldArrayId}`

            return (
              <Card key={fieldArrayId}>
                <CardHeader className="flex flex-row items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-base">Position {index + 1}</CardTitle>
                    <CardDescription>
                      Provide details about this role, including responsibilities and reporting line.
                    </CardDescription>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(index)}
                    className="text-destructive"
                  >
                    Remove
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name={`entries.${index}.organisation`}
                      render={({ field: controllerField, fieldState }) => (
                        <FormItem>
                          <FormLabel required>Organisation</FormLabel>
                          <FormControl>
                            <Input placeholder="UIBFS" data-invalid={fieldState.invalid} {...controllerField} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`entries.${index}.jobTitle`}
                      render={({ field: controllerField, fieldState }) => (
                        <FormItem>
                          <FormLabel required>Job title</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="e.g. Finance Manager"
                              data-invalid={fieldState.invalid}
                              {...controllerField}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name={`entries.${index}.startDate`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required>Start date</FormLabel>
                          <FormControl>
                            <DatePicker
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Select start date"
                              disableFuture
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`entries.${index}.endDate`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required={!currentlyEmployed}>End date</FormLabel>
                          <FormControl>
                            <DatePicker
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Select end date"
                              disabled={currentlyEmployed}
                              disableFuture
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name={`entries.${index}.currentlyEmployed`}
                    render={({ field: controllerField }) => (
                      <FormItem>
                        <FormControl>
                          <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 p-3">
                            <Checkbox
                              checked={controllerField.value}
                              onCheckedChange={(checked) => controllerField.onChange(checked === true)}
                              id={checkboxId}
                            />
                            <FormLabel htmlFor={checkboxId} className="text-sm font-medium">
                              I currently work in this role
                            </FormLabel>
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name={`entries.${index}.reportingTo`}
                      render={({ field: controllerField, fieldState }) => (
                        <FormItem>
                          <FormLabel>Reporting to (optional)</FormLabel>
                          <FormControl>
                            <Input placeholder="Line manager" data-invalid={fieldState.invalid} {...controllerField} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div />
                  </div>

                  <FormField
                    control={form.control}
                    name={`entries.${index}.responsibilities`}
                    render={({ field: controllerField, fieldState }) => (
                      <FormItem>
                        <FormLabel>Key responsibilities (optional)</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Summarise your responsibilities and achievements"
                            data-invalid={fieldState.invalid}
                            {...controllerField}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            )
          })}
        </div>

        {form.formState.errors.entries ? (
          <p className="text-sm text-destructive">{form.formState.errors.entries.message}</p>
        ) : null}

        <CardFooter className="flex justify-end">
          <Button type="submit" disabled={isBusy}>
            {isBusy ? "Saving..." : "Continue"}
          </Button>
        </CardFooter>
      </form>
    </Form>
  )
}
